import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { CheckCircle, CreditCard, Building2, Loader2, Shield, Award, FileText, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';
import EtapeInformations from './SouscriptionRCDS/EtapeInformations';
import EtapeDocuments from './SouscriptionRCDS/EtapeDocuments';
import EtapeSignature from './SouscriptionRCDS/EtapeSignature';
import EtapePaiement from './SouscriptionRCDS/EtapePaiement';

const API = '/api';

// Etapes du workflow (la simulation est désormais externalisée vers le Calculateur RCDS BATICO complet)
const ETAPES = {
  INFORMATIONS: 'informations',
  DOCUMENTS: 'documents',
  SIGNATURE: 'signature',
  PAIEMENT: 'paiement',
  ATTESTATION: 'attestation'
};

// Configuration des etapes pour l'indicateur premium
const ETAPES_CONFIG = [
  { key: 'INFORMATIONS', label: 'Informations', icon: Building2, description: 'Donnees entreprise' },
  { key: 'DOCUMENTS', label: 'Documents', icon: FileText, description: 'Consulter les CGV' },
  { key: 'SIGNATURE', label: 'Signature', icon: FileCheck, description: 'Signature electronique' },
  { key: 'PAIEMENT', label: 'Paiement', icon: CreditCard, description: 'Virement bancaire' },
  { key: 'ATTESTATION', label: 'Attestation', icon: Award, description: 'Certificat final' }
];

export default function SouscriptionWorkflow() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const resumeId = searchParams.get('resume');
  const { tarification, ca_ht, nombre_jours, activites_selectionnees, periode_personnalisee, taxes_personnalisees, montants_personnalises } = location.state || {};
  
  const [etapeCourante, setEtapeCourante] = useState(ETAPES.INFORMATIONS);
  const [souscriptionId, setSouscriptionId] = useState(resumeId || null);
  const [loading, setLoading] = useState(false);
  const [loadingResume, setLoadingResume] = useState(!!resumeId);
  const [simulationData, _setSimulationData] = useState(null);
  
  const [formData, setFormData] = useState({
    raison_sociale: '',
    siret: '',
    nom_gerant: '',
    prenom_gerant: '',
    adresse_entreprise: '',
    code_postal: '',
    ville: '',
    pays: 'France',
    chiffre_affaires: ca_ht || 0,
    effectif: '',
    montant_ht_contribution: montants_personnalises?.montant_ht_personnalise || tarification?.montant_ht || 0,
    statut_juridique: '',
    code_naf: '',
    territoire: ''
  });
  
  const [territoires, setTerritoires] = useState([]);
  const [telephone, setTelephone] = useState('');
  const [codeOtp, setCodeOtp] = useState('');
  const [codeOtpGenere, setCodeOtpGenere] = useState(null);
  const [otpEnvoye, setOtpEnvoye] = useState(false);
  const [_otpExpiration, setOtpExpiration] = useState(null);
  const [_documents, _setDocuments] = useState(null);
  const [referenceVirement, setReferenceVirement] = useState(null);

  // Etat FTPE
  const [ftpeAdhesionId, setFtpeAdhesionId] = useState(null);
  const [ftpeStatus, setFtpeStatus] = useState('pending');
  // Devis émis pour un souscripteur « déjà adhérent » : le bloc FTPE est masqué (Documents + Paiement)
  const [ftpeDejaAdherent, setFtpeDejaAdherent] = useState(false);
  useEffect(() => {
    const devisId = new URLSearchParams(window.location.search).get('proposition');
    if (!devisId || !getToken()) return;
    axios.get(`${API}/souscriptions/simulateur/ftpe-statut/${encodeURIComponent(devisId)}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setFtpeDejaAdherent(r.data?.deja_adherent === true)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!souscriptionId || !getToken()) return;
    axios.get(`${API}/souscriptions/${souscriptionId}/ftpe-deja-adherent`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => { if (r.data?.deja_adherent === true) setFtpeDejaAdherent(true); }).catch(() => {});
  }, [souscriptionId]);
  const [ftpeAccepted, setFtpeAccepted] = useState(false);
  const [ftpeTelephone, setFtpeTelephone] = useState('');
  const [ftpeOtpEnvoye, setFtpeOtpEnvoye] = useState(false);
  const [ftpeCodeOtp, setFtpeCodeOtp] = useState('');
  const [ftpeReferenceVirement, setFtpeReferenceVirement] = useState(null);

  // (handleSimulationComplete a été retiré : la simulation est désormais effectuée
  // dans /calculateur-batico, et la tarification est passée via location.state.)

  useEffect(() => {
    const allowedRoles = ['member', 'beneficiaire_pro', 'super_admin', 'admin', 'cooper'];
    if (!user || !allowedRoles.includes(user.role)) {
      toast.error('Acces non autorise');
      navigate('/calculateur-batico');
      return;
    }
    // Si aucune tarification (et pas de reprise de brouillon),
    // rediriger vers le Calculateur RCDS BATICO complet (avec scoring d'éligibilité,
    // blocage VALIDATION_MANUELLE, etc.) puis revenir ici avec le résultat.
    if (!resumeId && !tarification) {
      toast.info('Veuillez d\'abord calculer votre cotisation via le Calculateur RCDS BATICO.');
      navigate('/calculateur-batico?from=souscription');
    }
  }, [user, navigate, resumeId, tarification]);

  useEffect(() => {
    const fetchTerritoires = async () => {
      try {
        const response = await fetch(`/api/territoires`);
        if (response.ok) {
          const data = await response.json();
          setTerritoires(data);
        }
      } catch {}
    };
    fetchTerritoires();
  }, []);

  useEffect(() => {
    const loadExistingSouscription = async () => {
      if (!resumeId) return;
      setLoadingResume(true);
      try {
        const token = getToken();
        const response = await axios.get(`${API}/souscriptions-rcd/${resumeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const souscription = response.data;
        setFormData({
          raison_sociale: souscription.raison_sociale || '',
          siret: souscription.siret || '',
          nom_gerant: souscription.nom_gerant || '',
          prenom_gerant: souscription.prenom_gerant || '',
          adresse_entreprise: souscription.adresse_entreprise || '',
          code_postal: souscription.code_postal || '',
          ville: souscription.ville || '',
          pays: souscription.pays || 'France',
          chiffre_affaires: souscription.chiffre_affaires || 0,
          effectif: souscription.effectif || '',
          montant_ht_contribution: souscription.montant_ht_contribution || 0,
          statut_juridique: souscription.statut_juridique || '',
          code_naf: souscription.code_naf || '',
          territoire: souscription.territoire || ''
        });
        setReferenceVirement(souscription.reference_virement);
        if (souscription.signature?.signee) {
          setEtapeCourante(ETAPES.PAIEMENT);
        } else if (souscription.documents?.length > 0 || souscription.documents_ids?.length > 0) {
          setEtapeCourante(ETAPES.DOCUMENTS);
        }
        toast.success('Souscription chargee avec succes');
      } catch {
        toast.error('Erreur lors de la reprise de la souscription');
      } finally {
        setLoadingResume(false);
      }
    };
    loadExistingSouscription();
  }, [resumeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreerSouscription = async () => {
    const requiredFields = ['raison_sociale', 'siret', 'nom_gerant', 'prenom_gerant', 'adresse_entreprise', 'code_postal', 'ville', 'statut_juridique', 'code_naf', 'territoire'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (formData.siret.length !== 14) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const finalMontantHT = montants_personnalises?.montant_ht_personnalise || tarification?.montant_ht || simulationData?.montantHT || formData.montant_ht_contribution;
      const finalMontantTTC = montants_personnalises?.montant_ttc_personnalise || tarification?.montant_ttc || simulationData?.montantTTC || (finalMontantHT * 1.1);
      const payload = {
        ...formData,
        type_souscription: nombre_jours ? 'par_periode' : 'annuel',
        nombre_jours: nombre_jours,
        activites_selectionnees: activites_selectionnees || simulationData?.activitesSelectionnees || {},
        montant_ht: finalMontantHT,
        montant_ttc: finalMontantTTC,
        tarification: {
          montant_ht: finalMontantHT,
          montant_ttc: finalMontantTTC,
          taxes: taxes_personnalisees
            ? { detail: taxes_personnalisees, is_personnalise: true }
            : (tarification?.taxes || { tva: finalMontantHT * 0.2 }),
          taxes_personnalisees: taxes_personnalisees || null
        },
        ...(taxes_personnalisees && {
          taxes_personnalisees: taxes_personnalisees,
          taxes: { detail: taxes_personnalisees, is_personnalise: true }
        }),
        ...(periode_personnalisee && { periode_personnalisee }),
        ...(montants_personnalises && { montants_personnalises })
      };
      const response = await axios.post(`${API}/souscriptions-rcd`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSouscriptionId(response.data.souscription_id || response.data.id);
      setReferenceVirement(response.data.reference_virement);
      toast.success('Souscription creee avec succes !');
      setEtapeCourante(ETAPES.DOCUMENTS);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setLoading(false);
    }
  };

  const handleDemanderSignature = async () => {
    if (!telephone || telephone.length < 10) {
      toast.error('Veuillez entrer un numero de telephone valide');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/souscriptions-rcd/${souscriptionId}/signature/demander`,
        { telephone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOtpEnvoye(true);
      setOtpExpiration(Date.now() + 10 * 60 * 1000);
      if (response.data.code_otp) {
        setCodeOtpGenere(response.data.code_otp);
        toast.success(`Code de signature genere : ${response.data.code_otp}`, { duration: 15000 });
      } else {
        toast.success('Code de signature envoye par SMS');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du code");
    } finally {
      setLoading(false);
    }
  };

  const handleValiderSignature = async () => {
    if (!codeOtp || codeOtp.length !== 6) {
      toast.error('Veuillez entrer le code a 6 chiffres');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/souscriptions-rcd/${souscriptionId}/signature/valider`,
        { code_otp: codeOtp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Signature validee ! Passez au paiement.');
      setEtapeCourante(ETAPES.PAIEMENT);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide ou expire');
    } finally {
      setLoading(false);
    }
  };

  // === Handlers FTPE ===
  const handleCreateFtpeAdhesion = async () => {
    try {
      const token = getToken();
      const payload = {
        raison_sociale: formData.raison_sociale,
        siret: formData.siret,
        representant_nom: `${formData.prenom_gerant} ${formData.nom_gerant}`,
        representant_email: user?.email || '',
        representant_telephone: telephone || '',
        adresse: formData.adresse_entreprise,
        code_postal: formData.code_postal,
        ville: formData.ville,
        territoire: formData.territoire,
        telephone: telephone || '0000000000',
        email: user?.email || '',
        code_ape: formData.code_naf || '',
        forme_juridique: formData.statut_juridique || '',
        type_adhesion: 'tpe',
      };
      const response = await axios.post(`${API}/ftpe-adhesion/workflow/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const adhesionId = response.data.adhesion_id || response.data.id;
      setFtpeAdhesionId(adhesionId);
      setFtpeAccepted(true);
      setFtpeStatus('created');
      toast.success('Adhesion FTPE creee avec succes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation FTPE');
    }
  };

  const handleFtpeSendOtp = async () => {
    if (!ftpeTelephone || ftpeTelephone.length < 10) {
      toast.error('Veuillez entrer un numero de telephone valide');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/send-sms`,
        { telephone: ftpeTelephone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFtpeOtpEnvoye(true);
      if (response.data.code_otp) {
        toast.success(`Code FTPE genere : ${response.data.code_otp}`, { duration: 15000 });
      } else {
        toast.success('Code FTPE envoye par SMS');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi SMS FTPE');
    } finally {
      setLoading(false);
    }
  };

  const handleFtpeValidateSignature = async () => {
    if (!ftpeCodeOtp || ftpeCodeOtp.length < 4) {
      toast.error('Veuillez entrer le code de validation');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/validate-signature`,
        { code_otp: ftpeCodeOtp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFtpeStatus('signed');
      toast.success('Signature FTPE validee ! Vous pouvez maintenant signer la souscription RCDS.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code FTPE invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleFtpePaymentVirement = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/payment-virement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFtpeReferenceVirement(response.data.reference_virement || response.data.reference);
      setFtpeStatus('paid');
      toast.success('Paiement FTPE par virement enregistre');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur paiement FTPE');
    } finally {
      setLoading(false);
    }
  };

  // Calculer l'index de l'etape actuelle
  const currentStepIndex = ETAPES_CONFIG.findIndex(e => ETAPES[e.key] === etapeCourante);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Overlay pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySC0yNHYtMmgxMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
      
      <div className="relative z-10">
        {/* Header Premium */}
        <div className="border-b border-amber-500/20 bg-black/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <BackButton to="/calculateur-batico" label="Retour a l'estimateur" homeLabel="Cockpit BATICO / RCDS" className="text-slate-300 hover:text-white" />
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-amber-500" />
                <span className="text-sm text-slate-400">Processus securise</span>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loadingResume && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-800 border border-amber-500/30 p-10 rounded-2xl shadow-2xl text-center max-w-md">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-xl"></div>
                <Loader2 className="h-16 w-16 animate-spin text-amber-500 mx-auto relative" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Reprise de votre souscription</h3>
              <p className="text-slate-400">Chargement des informations en cours...</p>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-6 pt-3 pb-4">
          {/* Stepper Premium */}
          <div className="mb-12">
            <div className="relative">
              <div className="absolute top-8 left-0 right-0 h-0.5 bg-slate-700 hidden lg:block" style={{ left: '8%', right: '8%' }}></div>
              <div 
                className="absolute top-8 h-0.5 bg-gradient-to-r from-amber-500 to-amber-600 hidden lg:block transition-all duration-500" 
                style={{ left: '8%', width: `${Math.max(0, (currentStepIndex / (ETAPES_CONFIG.length - 1)) * 84)}%` }}
              ></div>
              
              <div className="flex justify-between items-start">
                {ETAPES_CONFIG.map((step, index) => {
                  const isActive = index === currentStepIndex;
                  const isCompleted = index < currentStepIndex;
                  const Icon = step.icon;
                  return (
                    <div key={step.key} className="flex flex-col items-center relative z-10 flex-1">
                      <div className={`
                        w-16 h-16 rounded-2xl flex items-center justify-center mb-3 transition-all duration-300
                        ${isActive ? 'bg-gradient-to-br from-amber-500 to-amber-600 shadow-lg shadow-amber-500/30 scale-110' : 
                          isCompleted ? 'bg-emerald-500/20 border-2 border-emerald-500' : 
                          'bg-slate-800 border-2 border-slate-700'}
                      `}>
                        {isCompleted ? (
                          <CheckCircle className="h-7 w-7 text-emerald-400" />
                        ) : (
                          <Icon className={`h-7 w-7 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                        )}
                      </div>
                      <span className={`text-sm font-semibold mb-1 ${isActive ? 'text-amber-400' : isCompleted ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {step.label}
                      </span>
                      <span className="text-xs text-slate-500 text-center hidden md:block">
                        {step.description}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto">
            {etapeCourante === ETAPES.INFORMATIONS && (
              <EtapeInformations 
                formData={formData}
                setFormData={setFormData}
                handleInputChange={handleInputChange}
                tarification={tarification || simulationData}
                activites_selectionnees={activites_selectionnees}
                handleCreerSouscription={handleCreerSouscription}
                loading={loading}
                territoires={territoires}
                montants_personnalises={montants_personnalises}
              />
            )}

            {etapeCourante === ETAPES.DOCUMENTS && (
              <EtapeDocuments 
                souscriptionId={souscriptionId}
                setEtapeCourante={setEtapeCourante}
                ftpeAccepted={ftpeAccepted || ftpeDejaAdherent}
                ftpeDejaAdherent={ftpeDejaAdherent}
                ftpeAdhesionId={ftpeAdhesionId}
                handleCreateFtpeAdhesion={handleCreateFtpeAdhesion}
                loading={loading}
              />
            )}

            {etapeCourante === ETAPES.SIGNATURE && (
              <EtapeSignature 
                telephone={telephone}
                setTelephone={setTelephone}
                codeOtp={codeOtp}
                setCodeOtp={setCodeOtp}
                codeOtpGenere={codeOtpGenere}
                otpEnvoye={otpEnvoye}
                setOtpEnvoye={setOtpEnvoye}
                handleDemanderSignature={handleDemanderSignature}
                handleValiderSignature={handleValiderSignature}
                loading={loading}
                souscriptionId={souscriptionId}
                ftpeStatus={ftpeStatus}
                ftpeAdhesionId={ftpeAdhesionId}
                ftpeTelephone={ftpeTelephone}
                setFtpeTelephone={setFtpeTelephone}
                ftpeOtpEnvoye={ftpeOtpEnvoye}
                ftpeCodeOtp={ftpeCodeOtp}
                setFtpeCodeOtp={setFtpeCodeOtp}
                handleFtpeSendOtp={handleFtpeSendOtp}
                handleFtpeValidateSignature={handleFtpeValidateSignature}
              />
            )}

            {etapeCourante === ETAPES.PAIEMENT && (
              <EtapePaiement 
                ftpeDejaAdherent={ftpeDejaAdherent}
                referenceVirement={referenceVirement}
                montant={tarification?.montant_ttc || simulationData?.montantTTC}
                ftpeStatus={ftpeStatus}
                ftpeReferenceVirement={ftpeReferenceVirement}
                handleFtpePaymentVirement={handleFtpePaymentVirement}
                loading={loading}
                souscriptionId={souscriptionId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
